import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import AdminService from '../../service/AdminService';
import { useSelector, useDispatch } from 'react-redux';
import { clearSelectedProgram, updateProgram as updateProgramAction } from '../../slice/programSlice';
import toast from 'react-hot-toast';
import Loader from '../loader/Loader';

const schema = yup.object().shape({
  title: yup.string()
    .required('Title is required')
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must be at most 100 characters'),
  brief: yup.string()
    .required('Brief description is required')
    .min(10, 'Brief must be at least 10 characters')
    .max(255, 'Brief must be at most 255 characters'),
  imageUrl: yup.string()
    .required('Image URL is required')
    .url('Must be a valid URL'),
  price: yup.number()
    .required('Price is required')
    .min(0, 'Price must be positive'),
  sellingPrice: yup.number()
    .required('Selling price is required')
    .min(0, 'Selling price must be positive')
    .test('sellingPrice', 'Selling price must be less than or equal to price', 
      function(value) {
        return value <= this.parent.price;
    }),
  trimesterNo: yup.number()
    .required('Trimester number is required')
    .min(1, 'Trimester must be between 1 and 3')
    .max(3, 'Trimester must be between 1 and 3'),
  enrollmentCapacity: yup.number()
    .required('Enrollment capacity is required')
    .min(1, 'Capacity must be at least 1'),
  startDate: yup.date()
    .required('Start date is required')
    .typeError("Start date must be a valid date"),
  premium: yup.boolean()
    .required('Premium status is required'),
  active: yup.boolean()
    .required('Active status is required'),
});

const EditProgram = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { programId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector((state) => state.user.user);
  const selectedProgram = useSelector((state) => state.program.selectedProgram);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      title: "",
      brief: "",
      description: "",
      imageUrl: "",
      price: 0,
      sellingPrice: 0,
      trimesterNo: 1,
      enrollmentCapacity: 30,
      startDate: new Date().toISOString().split('T')[0],
      premium: false,
      active: true
    }
  });

  useEffect(() => {
    if (selectedProgram) {
      reset({
        title: selectedProgram.title,
        brief: selectedProgram.brief,
        description: selectedProgram.description,
        imageUrl: selectedProgram.imageUrl,
        price: selectedProgram.price,
        sellingPrice: selectedProgram.sellingPrice,
        trimesterNo: selectedProgram.trimesterNo,
        enrollmentCapacity: selectedProgram.enrollmentCapacity,
        startDate: selectedProgram.startDate,
        premium: selectedProgram.premium,
        active: selectedProgram.active
      });
    } else {
      navigate('/admin/programs');
    }
    
    return () => {
      dispatch(clearSelectedProgram());
    };
  }, [selectedProgram, reset, navigate, dispatch]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      const startDate = new Date(data.startDate);
      startDate.setHours(12, 0, 0, 0);
      
      const programData = {
        ...data,
        programId: parseInt(programId),
        startDate: startDate.toISOString().split('T')[0]
      };

      const response = await AdminService.updateProgram(user?.access_token, programData);
      dispatch(updateProgramAction(response.data));
      toast.success('Program updated successfully', {duration: 4000});
      navigate('/admin/programs');
    } catch (error) {
      console.error('Error updating program:', error);
      toast.error(error.response?.data?.message || 'Error updating program', {duration: 4000});
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-gray-900">Edit Program</h1>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="bg-white border-b py-3">
          <h3 className="text-xs font-medium text-gray-900">Program Information</h3>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Title <span className="text-red-500">*</span></Label>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className={`mt-1 ${errors.title ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.title && (
                  <span className="text-xs text-red-500">{errors.title.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Brief Description <span className="text-red-500">*</span></Label>
                <Controller
                  name="brief"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      className={`mt-1 ${errors.brief ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.brief && (
                  <span className="text-xs text-red-500">{errors.brief.message}</span>
                )}
              </FormGroup>

              <FormGroup className="md:col-span-2">
                <Label className="text-xs font-medium text-gray-700">Full Description</Label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      rows="4"
                      className={`mt-1 ${errors.description ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.description && (
                  <span className="text-xs text-red-500">{errors.description.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Image URL <span className="text-red-500">*</span></Label>
                <Controller
                  name="imageUrl"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className={`mt-1 ${errors.imageUrl ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.imageUrl && (
                  <span className="text-xs text-red-500">{errors.imageUrl.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Price (₹) <span className="text-red-500">*</span></Label>
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      className={`mt-1 ${errors.price ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.price && (
                  <span className="text-xs text-red-500">{errors.price.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Selling Price (₹) <span className="text-red-500">*</span></Label>
                <Controller
                  name="sellingPrice"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      className={`mt-1 ${errors.sellingPrice ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.sellingPrice && (
                  <span className="text-xs text-red-500">{errors.sellingPrice.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Trimester Number <span className="text-red-500">*</span></Label>
                <Controller
                  name="trimesterNo"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`mt-1 ${errors.trimesterNo ? "border-red-500" : ""}`}
                    >
                      <option value="1">First Trimester</option>
                      <option value="2">Second Trimester</option>
                      <option value="3">Third Trimester</option>
                    </Input>
                  )}
                />
                {errors.trimesterNo && (
                  <span className="text-xs text-red-500">{errors.trimesterNo.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Enrollment Capacity <span className="text-red-500">*</span></Label>
                <Controller
                  name="enrollmentCapacity"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      className={`mt-1 ${errors.enrollmentCapacity ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.enrollmentCapacity && (
                  <span className="text-xs text-red-500">{errors.enrollmentCapacity.message}</span>
                )}
              </FormGroup>

              <FormGroup>
                <Label className="text-xs font-medium text-gray-700">Start Date <span className="text-red-500">*</span></Label>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="date"
                      className={`mt-1 ${errors.startDate ? "border-red-500" : ""}`}
                    />
                  )}
                />
                {errors.startDate && (
                  <span className="text-xs text-red-500">{errors.startDate.message}</span>
                )}
              </FormGroup>

              <FormGroup className="flex items-center mt-4">
                <Controller
                  name="premium"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="h-4 w-4 text-teal-600"
                    />
                  )}
                />
                <Label className="ml-2 text-xs font-medium text-gray-700">Premium Program</Label>
                {errors.premium && (
                  <span className="text-xs text-red-500 ml-2">{errors.premium.message}</span>
                )}
              </FormGroup>

              <FormGroup className="flex items-center mt-4">
                <Controller
                  name="active"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="h-4 w-4 text-teal-600"
                    />
                  )}
                />
                <Label className="ml-2 text-xs font-medium text-gray-700">Active</Label>
                {errors.active && (
                  <span className="text-xs text-red-500 ml-2">{errors.active.message}</span>
                )}
              </FormGroup>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <Button
                type="submit"
                color="primary"
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg transition-colors duration-200 border-0"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update Program"}
              </Button>
              <Button
                type="button"
                color="danger"
                className="text-white px-6 py-2 rounded-lg transition-colors duration-200 border-0"
                onClick={() => navigate("/admin/programs")}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
      {isLoading && <Loader />}
    </div>
  );
};

export default EditProgram; 